import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, DollarSign, TrendingUp, ShoppingCart, PercentIcon } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { apiService, Department, ExecutiveDashboardData, GrowthEnginesData } from '../services/api';
import { DepartmentFilter, ExclusionBanner } from '../components/dashboard/DepartmentFilter';
import { Button } from '../components/ui/button';
import { PeriodSelector } from '../components/dashboard/PeriodSelector';
import { KpiCard } from '../components/dashboard/KpiCard';
import { RevenueTrendChart } from '../components/dashboard/RevenueTrendChart';
import { DepartmentTable } from '../components/dashboard/DepartmentTable';
import { DepartmentDonut } from '../components/dashboard/DepartmentDonut';
import { AlertsPanel } from '../components/dashboard/AlertsPanel';
import { RecentActivity } from '../components/dashboard/RecentActivity';
import { GrowthEngines } from '../components/dashboard/GrowthEngines';
import toast from 'react-hot-toast';

const SectionHeader: React.FC<{ title: string; note?: string }> = ({ title, note }) => (
  <div className="flex items-baseline gap-2 pt-2">
    <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">{title}</h2>
    {note && <span className="text-xs text-gray-400">{note}</span>}
  </div>
);

const EXCLUDED_DEPTS_KEY = 'dashboard.excludedDepts';

const loadStoredExclusions = (): string[] => {
  try {
    const raw = localStorage.getItem(EXCLUDED_DEPTS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter(x => typeof x === 'string') : [];
  } catch {
    return [];
  }
};

const Dashboard: React.FC = () => {
  const [period, setPeriod] = useState('mtd');
  const [customStart, setCustomStart] = useState<string>();
  const [customEnd, setCustomEnd] = useState<string>();
  const [data, setData] = useState<ExecutiveDashboardData | null>(null);
  const [engines, setEngines] = useState<GrowthEnginesData | null>(null);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [excludedDepts, setExcludedDepts] = useState<string[]>(loadStoredExclusions);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const result = await apiService.getExecutiveDashboard(period, customStart, customEnd, excludedDepts);
      if (result) setData(result);
    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [period, customStart, customEnd, excludedDepts]);

  useEffect(() => { loadData(); }, [loadData]);

  // Department list for the exclusion filter
  useEffect(() => {
    apiService.loadDepartments().then(() => {
      setDepartments(apiService.getDepartments().filter(d => d.is_active !== false && d.name !== 'Uncategorized'));
    });
  }, []);

  const handleExclusionsChange = (next: string[]) => {
    setExcludedDepts(next);
    try { localStorage.setItem(EXCLUDED_DEPTS_KEY, JSON.stringify(next)); } catch { /* private mode */ }
  };

  // Growth engines are trailing-month views — independent of the period selector
  useEffect(() => {
    apiService.getGrowthEngines().then(result => {
      if (result) setEngines(result);
    });
  }, []);

  const handlePeriodChange = (newPeriod: string, start?: string, end?: string) => {
    setPeriod(newPeriod);
    setCustomStart(start);
    setCustomEnd(end);
  };

  const handleSyncAll = async () => {
    setSyncing(true);
    try {
      await apiService.loadPlatforms();
      const platforms = apiService.getPlatforms();
      await Promise.all(platforms.map(p => apiService.syncPlatform(p.id)));
      toast.success('All platforms synced');
      await loadData();
    } catch {
      toast.error('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const formatDateRange = () => {
    if (!data) return '';
    try {
      const start = format(parseISO(data.period.start), 'MMM d, yyyy');
      const end = format(parseISO(data.period.end), 'MMM d, yyyy');
      const cStart = format(parseISO(data.period.comparisonStart), 'MMM d, yyyy');
      const cEnd = format(parseISO(data.period.comparisonEnd), 'MMM d, yyyy');
      return `${start} - ${end}  vs  ${cStart} - ${cEnd}`;
    } catch {
      return '';
    }
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-500">Loading dashboard...</span>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Executive Dashboard</h1>
          <p className="text-xs text-gray-400 mt-0.5">{formatDateRange()}</p>
        </div>
        <div className="flex items-center gap-3">
          <PeriodSelector selected={period} onSelect={handlePeriodChange} />
          <DepartmentFilter
            departments={departments}
            excluded={excludedDepts}
            onChange={handleExclusionsChange}
          />
          <Button onClick={handleSyncAll} disabled={syncing} className="bg-blue-600 hover:bg-blue-700 flex-shrink-0">
            <RefreshCw className={`h-4 w-4 mr-1.5 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync'}
          </Button>
        </div>
      </div>

      {/* Loading overlay for period switches */}
      {loading && data && (
        <div className="flex items-center gap-2 text-sm text-blue-600">
          <RefreshCw className="h-4 w-4 animate-spin" />
          Updating...
        </div>
      )}

      {/* Active-filter banner */}
      <ExclusionBanner
        departments={departments}
        excluded={excludedDepts}
        onClear={() => handleExclusionsChange([])}
      />

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          title="Net Revenue"
          metric={data.kpis.netRevenue}
          format="currency"
          sparkData={data.dailySeries}
          sparkKey="netRevenue"
          icon={<DollarSign size={18} />}
          accent="blue"
        />
        <KpiCard
          title="Transactions"
          metric={data.kpis.txCount}
          format="number"
          sparkData={data.dailySeries}
          sparkKey="txCount"
          icon={<ShoppingCart size={18} />}
          accent="violet"
        />
        <KpiCard
          title="Avg Order Value"
          metric={data.kpis.avgOrder}
          format="currency"
          icon={<TrendingUp size={18} />}
          accent="emerald"
        />
        <KpiCard
          title="Refund Rate"
          metric={data.kpis.refundRate}
          format="percent"
          invertColor={true}
          icon={<PercentIcon size={18} />}
          accent="amber"
        />
      </div>

      {/* Revenue Trend Chart */}
      <RevenueTrendChart
        current={data.dailySeries}
        comparison={data.comparisonDailySeries}
        periodLabel={period}
      />

      {/* Growth Engines — trailing views, independent of the period selector */}
      {engines && (
        <>
          <SectionHeader title="Growth Engines" note="trailing months — not affected by the period filter" />
          <GrowthEngines data={engines} />
        </>
      )}

      {/* Department Performance + Donut */}
      <SectionHeader title="Departments" />
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3">
          <DepartmentTable departments={data.departments} />
        </div>
        <div className="lg:col-span-2">
          <DepartmentDonut departments={data.departments} />
        </div>
      </div>

      {/* Alerts + Recent Activity */}
      <SectionHeader title="Health & Activity" />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <AlertsPanel alerts={data.alerts} />
        <RecentActivity transactions={data.recentTransactions} />
      </div>
    </div>
  );
};

export default Dashboard;
